import type { Video } from '~/types/Learn/Video';

import { usePublicDetailFetch } from '~/composables/unified/usePublicDetailFetch';
import { fetchVideoBySlug, videoLearningAssetFragment } from '~/graphql/documents/learn/videos';
import { useFragment } from '~/graphql/generated';
import { LearningAssetType } from '~/graphql/generated/graphql';
import { transformVideoAsset } from '~/utils/transformers/videoTransformers';

/**
 * The video behind /videos/:slug. The public root serves published rows only, so a row whose
 * published content carries no video data is a video nobody can watch and is treated as missing —
 * gating on the content alone would resolve it and leave the page spinning on a 200.
 */
export const useVideoDetail = async (slug: string) => {
  const { $urqlClient } = useNuxtApp();

  const { data, fetching, error } = await usePublicDetailFetch({
    key: `video-detail-${slug}`,
    fetch: async () => {
      const result = await $urqlClient
        .query(fetchVideoBySlug, { filters: { slugs: [slug], types: [LearningAssetType.Video] } })
        .toPromise();
      if (result.error) {
        throw result.error;
      }
      return result.data;
    },
    isNotFound: (data) => {
      const row = data?.learningAssets?.data?.[0];
      return !row || !useFragment(videoLearningAssetFragment, row).publishedContent?.videoData;
    },
    notFoundError: () => new Error(`Video not found with slug: ${slug}`),
  });

  const video = computed((): Video | null => {
    const row = data.value?.learningAssets?.data?.[0];
    return row ? transformVideoAsset(row) : null;
  });

  return { video, fetching, error };
};
